/**
 * Good practice appendix list (acf.appendix_table).
 */

import Link from "next/link";
import {
  asArray,
  hasHtmlContent,
  OPSI_PART_SPACING,
  OPSI_EXTERNAL_LINK_PROPS,
  resolveOpsiUrl,
} from "../utils/opsiHelpers";

const OPSI_APPENDIX_ACCENT = "#993189";
const OPSI_APPENDIX_BG = "#993189";
const OPSI_APPENDIX_BORDER = "#fdf5f9";

const AppendixViewButton = ({ href, text }) =>
  href ? (
    <Link
      href={href}
      {...OPSI_EXTERNAL_LINK_PROPS}
      className="opsi-view-pill-button shrink-0 transition hover:opacity-90 !bg-[#fff]"
    >
      <span className="opsi-view-pill-button__label !text-[#993189]">{text}</span>
    </Link>
  ) : (
    <span
      className="opsi-view-pill-button shrink-0 cursor-default opacity-50"
      style={{ backgroundColor: OPSI_APPENDIX_ACCENT }}
      aria-disabled="true"
    >
      <span className="opsi-view-pill-button__label">{text}</span>
    </span>
  );

export const OpsiAppendixTable = ({
  data,
  className = `opsi-appendix-table ${OPSI_PART_SPACING}`,
}) => {
  const rows = asArray(data?.url_link).filter((row) =>
    hasHtmlContent(row?.good_practice_example),
  );
  const firstHeader = data?.header?.first_header ?? "Good practice example";
  const secondHeader = data?.header?.second_header ?? "Where is it from?";

  if (!rows.length) {
    return null;
  }

  return (
    <section
      className={`${className} opsi-appendix-table__panel rounded-[20px] px-5 py-6 md:px-10 md:py-10`}
      style={{ backgroundColor: OPSI_APPENDIX_BG }}
      data-opsi-part="appendix-table"
    >
      {/* Desktop column headers */}
      <div
        className="opsi-appendix-table__head hidden items-center gap-6 border-b py-5 md:flex md:gap-8 md:py-6"
        style={{ borderColor: OPSI_APPENDIX_BORDER }}
      >
        <span className="min-w-0 flex-1 !text-[1.375rem] !font-bold !text-[#fff]">
          {firstHeader}
        </span>
        <span className="min-w-0 flex-1 !text-left !text-[1rem] !font-bold !text-[#fff]">
          {secondHeader}
        </span>
        <span className="w-[110px] flex-none" aria-hidden="true" />
      </div>

      <ul className="opsi-appendix-table__list !m-0 !list-none !p-0">
        {rows.map((row, index) => {
          const href = resolveOpsiUrl(row?.button?.button_link);
          const buttonText = row?.button?.button_text || "View";
          const example = row?.good_practice_example || "";
          const source = row?.where_is_it_from || "";

          return (
            <li
              key={`${example}-${index}`}
              className="border-b py-5 md:py-6 bg-[#993189]"
            >
              {/* Mobile: stacked card row */}
              <div className="flex flex-col gap-3 md:hidden">
                <div className="flex items-start justify-between gap-4">
                  <span className="min-w-0 flex-1 !text-[1rem] !font-bold !leading-snug !text-[#fff]">
                    {example}
                  </span>
                  <AppendixViewButton href={href} text={buttonText} />
                </div>
                {source ? (
                  <div className="flex flex-col gap-1">
                    <span className="!text-[0.8125rem] !font-semibold !text-[#fff]">
                      {secondHeader}
                    </span>
                    <span className="!text-[0.9375rem] !font-normal !leading-snug !text-[#464646]">
                      {source}
                    </span>
                  </div>
                ) : null}
              </div>

              {/* Desktop: three-column row */}
              <div className="hidden items-center gap-8 md:flex">
                <span className="min-w-0 flex-1 !text-[1.125rem] !font-bold !leading-snug !text-[#fff]">
                  {example}
                </span>
                <span className="min-w-0 flex-1 !text-left !text-[1.125rem] !font-normal !leading-snug !text-[#fff]">
                  {source}
                </span>
                <AppendixViewButton href={href} text={buttonText} />
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
};
